import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_NARROW, CS_WEIGHT, CS_Z, csTokens } from 'components/CallScribe/callScribeTheme';
import { mixHex } from 'components/CallScribe/ui/color';

const LABEL_BLOCK = 22;

const useHomeStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      minHeight: 0,
      flex: '1 1 auto',
      overflow: 'hidden',
      isolation: 'isolate',
    },
    dialBar: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    dialBarBare: {
      '& $popover': { top: 52 + 4 },
      '& $callButton': { marginTop: 0 },
    },
    dialRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
    },
    combo: {
      position: 'relative',
      flex: '1 1 auto',
      minWidth: 0,
    },
    popover: {
      position: 'absolute',
      top: LABEL_BLOCK + 52 + 4,
      left: 0,
      right: 0,
      zIndex: CS_Z.popover,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 380,
      boxSizing: 'border-box',
      background: t.surface,
      border: `1px solid ${t.line}`,
      borderRadius: 14,
      boxShadow: t.shadowFloat,
      overflow: 'hidden',
      animation: '$popIn 200ms ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes popIn': {
      from: { opacity: 0, transform: 'translateY(-4px)' },
      to: { opacity: 1, transform: 'none' },
    },
    popoverList: {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      padding: '0 6px 6px',
    },
    popoverFoot: {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 8,
      padding: '6px 8px 6px 4px',
      borderTop: `1px solid ${t.line}`,
    },
    footAction: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 44,
      padding: '0 8px',
      border: 'none',
      borderRadius: 8,
      background: 'transparent',
      color: t.text2,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    footHints: {
      '@media (max-width: 459px)': { display: 'none' },
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      ...CS_FONT.caption,
      color: t.text2,
      '& span': { ...CS_FONT.caption, marginRight: 4 },
    },
    kbd: {
      fontFamily: 'inherit',
      fontSize: 11,
      lineHeight: '16px',
      fontWeight: CS_WEIGHT.strong,
      padding: '0 5px',
      borderRadius: 5,
      border: `1px solid ${t.lineStrong}`,
      background: t.surface2,
      color: t.text2,
    },
    srOnly: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
    callButton: {
      flex: '0 0 auto',
      boxSizing: 'border-box',
      width: 52,
      height: 52,
      marginTop: LABEL_BLOCK,
      border: 'none',
      borderRadius: '50%',
      background: t.call,
      color: t.callInk,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 150ms ease-out, transform 120ms ease-out',
      '&:hover': { background: mixHex(t.call, t.text, 0.86) },
      '&:active': { transform: 'scale(0.96)' },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&:disabled': {
        background: t.surface3,
        color: t.text2,
        cursor: 'not-allowed',
        transform: 'none',
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'background-color 150ms ease-out',
        '&:active': { transform: 'none' },
      },
      [CS_NARROW]: { width: 48, height: 48 },
    },
    fieldButton: {
      flex: '0 0 auto',
      width: 44,
      height: 44,
      display: 'inline-grid',
      placeItems: 'center',
      border: 'none',
      borderRadius: 10,
      background: 'transparent',
      color: t.text2,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 0 },
      [CS_NARROW]: { width: 36 },
    },
    fieldKeypad: {
      [CS_NARROW]: { width: 32 },
    },
    pickerState: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
      ...CS_FONT.caption,
      color: t.text2,
      padding: '0 4px',
      '& span': { ...CS_FONT.caption },
    },
    pickerStateAction: {
      border: 'none',
      background: 'transparent',
      padding: '2px 0',
      fontFamily: 'inherit',
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.text,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    countrySelect: {
      flex: '0 0 auto',
      '& .MuiInputBase-root': {
        height: 44,
        position: 'relative',
        isolation: 'isolate',
        borderRadius: 8,
        background: 'transparent',
        color: t.text,
        fontFamily: 'inherit',
      },
      '& .MuiInputBase-root::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 5,
        bottom: 5,
        zIndex: -1,
        borderRadius: 8,
        background: t.surface3,
        pointerEvents: 'none',
      },
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        padding: '0 26px 0 8px !important',
        fontSize: 14,
        lineHeight: '44px',
        fontWeight: CS_WEIGHT.strong,
        minHeight: 0,
      },
      '& .MuiSelect-icon': { right: 2, color: t.text2 },
      [CS_NARROW]: {
        '& .MuiSelect-select': { padding: '0 6px !important' },
        '& .MuiSelect-icon': { display: 'none' },
      },
      '& .MuiOutlinedInput-notchedOutline': { border: 'none', top: 0, bottom: 5, borderRadius: 8 },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `2px solid ${t.focus}`,
      },
    },
    countryOption: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      ...CS_FONT.small,
      minWidth: 0,
    },
    countryOptionName: {
      ...CS_FONT.small,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    countryOptionDial: {
      ...CS_FONT.small,
      color: t.text2,
      flex: '0 0 auto',
    },
  };
});

export default useHomeStyles;
